import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw, FiWifi, FiWifiOff, FiLock, FiUnlock, FiPower, FiActivity, FiMonitor } from 'react-icons/fi';
import api from '../services/api';

const eventConfig: any = {
  online: { icon: FiWifi, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Came Online' },
  offline: { icon: FiWifiOff, color: 'text-slate-400', bg: 'bg-slate-500/20', label: 'Went Offline' },
  block: { icon: FiLock, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Blocked' },
  unblock: { icon: FiUnlock, color: 'text-green-400', bg: 'bg-green-500/20', label: 'Unblocked' },
  shutdown: { icon: FiPower, color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Shutdown' },
  hostname_change: { icon: FiActivity, color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Hostname Changed' },
  registered: { icon: FiMonitor, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Registered' },
};

const DeviceHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<any[]>([]);
  const [device, setDevice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchHistory = async () => {
    try {
      const [histRes, devRes] = await Promise.all([
        api.get(`/history/${id}`),
        api.get(`/devices/${id}`),
      ]);
      setLogs(histRes.data);
      setDevice(devRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 10000);
    return () => clearInterval(interval);
  }, [id]);

  const filteredLogs = filter === 'all' ? logs : logs.filter(l => l.event_type === filter);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-slate-400">Loading...</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/devices')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors p-2 bg-slate-800 rounded-lg">
          <FiArrowLeft className="text-xl" />
        </button>
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-3 rounded-xl">
            <FiMonitor className="text-white text-xl" />
          </div>
          <div>
            <h1 className="text-white text-2xl font-bold">{device?.hostname || 'Unknown Device'}</h1>
            <p className="text-slate-400 text-sm">
              {device?.os_type} • {device?.ip_address || 'No IP'} • {device?.group_name || 'No group'}
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm ${
            device?.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-400'
          }`}>
            {device?.status === 'online' ? '🟢 Online' : '⚫ Offline'}
          </span>
          <button onClick={fetchHistory}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors">
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 rounded-2xl p-4 text-center">
          <p className="text-slate-400 text-sm mb-1">Total Events</p>
          <p className="text-white text-2xl font-bold">{logs.length}</p>
        </div>
        <div className="bg-slate-800 rounded-2xl p-4 text-center">
          <p className="text-slate-400 text-sm mb-1">Online Events</p>
          <p className="text-green-400 text-2xl font-bold">{logs.filter(l => l.event_type === 'online').length}</p>
        </div>
        <div className="bg-slate-800 rounded-2xl p-4 text-center">
          <p className="text-slate-400 text-sm mb-1">Offline Events</p>
          <p className="text-slate-400 text-2xl font-bold">{logs.filter(l => l.event_type === 'offline').length}</p>
        </div>
        <div className="bg-slate-800 rounded-2xl p-4 text-center">
          <p className="text-slate-400 text-sm mb-1">Last Seen</p>
          <p className="text-white text-xs font-bold mt-1">
            {device?.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'online', 'offline', 'block', 'unblock', 'shutdown'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              filter === f ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
            }`}>
            {f === 'all' ? 'All Events' : eventConfig[f]?.label || f}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {filteredLogs.length === 0 ? (
        <div className="bg-slate-800 rounded-2xl p-12 text-center">
          <FiActivity className="text-slate-600 text-6xl mx-auto mb-4" />
          <p className="text-slate-400 text-lg">No events yet</p>
          <p className="text-slate-500 text-sm mt-1">Events will appear here when the device connects</p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-2xl overflow-hidden">
          {filteredLogs.map((log, index) => {
            const config = eventConfig[log.event_type] || {
              icon: FiActivity,
              color: 'text-slate-400',
              bg: 'bg-slate-500/20',
              label: log.event_type
            };
            const Icon = config.icon;
            return (
              <div key={log.id}
                className={`flex items-start gap-4 p-4 ${
                  index !== filteredLogs.length - 1 ? 'border-b border-slate-700/50' : ''
                } hover:bg-slate-700/30 transition-colors`}>
                <div className={`${config.bg} p-2 rounded-lg flex-shrink-0 mt-0.5`}>
                  <Icon className={`${config.color} text-lg`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className={`font-semibold ${config.color}`}>{config.label}</p>
                    <p className="text-slate-500 text-sm">{new Date(log.created_at).toLocaleString()}</p>
                  </div>
                  {log.details && (
                    <div className="mt-1 space-y-0.5">
                      {log.details.hostname && (
                        <p className="text-slate-400 text-sm">Host: {log.details.hostname}</p>
                      )}
                      {log.details.ip && (
                        <p className="text-slate-400 text-sm">IP: {log.details.ip}</p>
                      )}
                      {log.details.old_hostname && (
                        <p className="text-slate-400 text-sm">
                          {log.details.old_hostname} → {log.details.new_hostname}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeviceHistory;
